import { useEffect, useMemo, useCallback, useRef } from 'react';
import getModels from '../../api/cars/getModels';
import createModel from '../../api/cars/createModel';
import Select from '../Select/Select';
import usePaginatedSelect from '../../hooks/usePaginatedSelect';
import DictionaryNameModal from '../Modal/DictionaryNameModal';
import useDictionaryCreator from './useDictionaryCreator';

const extractItems = (res) => res.data.models;
const formatOption = (item) => ({ value: item.id, label: item.name });

/**
 * @param {boolean} used — б/у-процесс: обычные модели плюс заведённые под б/у,
 * с возможностью завести новую прямо здесь.
 */
const SelectModels = ({
  handleSetSelect,
  handleSetState,
  params,
  style,
  dependantValue,
  dependencyAccessor = (obj) => obj,
  onChangeCustom,
  used,
  creatable,
  ...props
}) => {
  const depValue = useMemo(() => dependencyAccessor(dependantValue), [dependantValue, dependencyAccessor]);

  const fetchFn = useCallback(
    (page) => getModels({ markId: depValue, page, used: used || undefined }),
    [depValue, used]
  );

  const { data, isLoading, loadMore, reset } = usePaginatedSelect({ fetchFn, extractItems, formatOption });

  const onFocus = useCallback(() => {
    if (!depValue) return;
    loadMore();
  }, [depValue, loadMore]);

  const create = useCallback(
    async (name) => {
      if (!depValue) throw new Error('Сначала выберите марку');
      // Контроллер (CarsController.createModel) на успехе отдаёт { markId },
      // в котором лежит id именно созданной модели.
      const res = await createModel({ markId: depValue, name, used: !!used });
      const id = Number(res?.data?.markId);
      if (!id) throw new Error('Не удалось добавить модель');
      return { value: id, label: name };
    },
    [depValue, used]
  );

  const { extraOptions, selectProps, modalProps } = useDictionaryCreator({
    // Без марки модель заводить не к чему — кнопку в списке не показываем.
    enabled: Boolean((creatable ?? used) && depValue),
    kind: 'model',
    create,
    onChange: props?.onChange,
    onChangeCustom: (val) => onChangeCustom && onChangeCustom(val, dependantValue),
    resetKey: depValue,
    context: dependantValue?.label && `Марка: ${dependantValue.label}`,
  });

  const options = useMemo(() => [...extraOptions, ...data], [extraOptions, data]);

  const isFirstRun = useRef(true);

  useEffect(() => {
    reset();
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (props?.onChange) {
      props.onChange(null);
    }
  }, [depValue]);

  return (
    <>
      <Select
        handleSetSelect={(val) => handleSetSelect && handleSetSelect(val)}
        handleSetState={handleSetState}
        options={options}
        title={'Модель'}
        placeholder={'Выберите из списка'}
        onFocus={onFocus}
        style={style}
        isLoading={isLoading}
        onChangeCustom={(val) => {
          if (onChangeCustom) {
            onChangeCustom(val, dependantValue);
          }
        }}
        {...props}
        {...selectProps}
      />
      {selectProps && <DictionaryNameModal {...modalProps} />}
    </>
  );
};

export default SelectModels;
